import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { grantChildSignIn, hasCognitoSession, type Me } from '@volee/api-client'
import type { DeviceAccount, DeviceAccounts } from '@volee/auth'
import { INTERNAL_MAIL_DOMAIN } from '@volee/config'
import { toast } from '@volee/ui'
import { useCallback, useEffect, useSyncExternalStore } from 'react'

import { useMutationAskingThePassword, withRecentPassword } from './recent-password.ts'
import { messages } from '../messages/index.ts'
import { NAV_PATHS } from '../navigation/paths.ts'
import { api } from '../services/api.ts'
import { auth } from '../services/auth.ts'

// The accounts open on this phone (spec #278, ticket #302): which one is in use, passing from one to
// another without a password, and a tutore adding a child. The tokens stay in @volee/auth.

export function useDeviceAccounts(): DeviceAccounts {
  return useSyncExternalStore(auth.accounts.subscribe, auth.accounts.get)
}

/** What the menu calls an account: the child's name, a username rather than the mail behind it, or
 *  the address. */
export function accountName(account: DeviceAccount): string {
  if (account.name !== undefined) return account.name
  const [local, domain] = account.email.split('@')
  return domain === INTERNAL_MAIL_DOMAIN && local !== undefined ? local : account.email
}

/** The account in use, once the device knows it. */
export function accountInUse(accounts: DeviceAccounts): DeviceAccount | undefined {
  return accounts.list.find((account) => account.sub === accounts.active)
}

/** More than one account on the phone, one of them in use: «Esci» then names which, and offers all. */
export function holdsSeveralAccounts(accounts: DeviceAccounts): boolean {
  return accounts.list.length > 1 && accountInUse(accounts) !== undefined
}

/** After the account in use changes nothing read as the other may stay, and the door says where
 *  this one lands: its profile, its request's status page. */
export function useLandAsAnother(): () => Promise<void> {
  const cache = useQueryClient()
  const navigate = useNavigate()
  return useCallback(async () => {
    cache.clear()
    await navigate({ to: NAV_PATHS.home, search: {}, replace: true })
  }, [cache, navigate])
}

export function useSwitchAccount() {
  const land = useLandAsAnother()
  return useMutation({
    mutationFn: (sub: string) => auth.accounts.switchTo(sub),
    onSuccess: land,
    // The account left the device on its way: what is left in the menu is what still works.
    onError: () => toast.error(messages.userMenu.switchFailed),
  })
}

/** «Usa questo telefono anche per …»: the tutore's password again (#301), the API's proof, then Cognito's
 *  custom challenge. The tutore stays the account in use. */
export function useAddChildToThisPhone(personId: number, name: string) {
  return useMutationAskingThePassword({
    mutationFn: async () => {
      const grant = await withRecentPassword(() => grantChildSignIn(api, personId))
      return auth.accounts.addDelegated(
        { username: grant.username, answer: grant.answer },
        { name, personId },
      )
    },
  })
}

/** The child's account on this phone, if a tutore already added it. */
export function useChildOnThisPhone(personId: number): DeviceAccount | undefined {
  return useDeviceAccounts().list.find(
    (account) => account.kind === 'delegated' && account.personId === personId,
  )
}

/** Whether the cached `me` is the account in use: right after a switch it may still be the other's. */
function isTheAccountInUse(me: Me, inUse: DeviceAccount | undefined): inUse is DeviceAccount {
  return inUse !== undefined && inUse.email.toLowerCase() === me.email.toLowerCase()
}

/** At 14 the child's account leaves the tutore's phone: a tutela no longer in force puts it there
 *  no more. `RevokeToken`, so the child's own phone stays signed in. */
export function useAccountsTheGuardianshipNoLongerCovers(me: Me): void {
  const accounts = useDeviceAccounts()
  useEffect(() => {
    const inUse = accountInUse(accounts)
    if (!hasCognitoSession(me) || !isTheAccountInUse(me, inUse)) return
    for (const account of accounts.list) {
      const covered = account.personId !== undefined && me.guardian_of.includes(account.personId)
      if (account.kind === 'delegated' && account.addedBy === inUse.sub && !covered) {
        // A failed revocation still takes it off the phone; the token lives at most its 90 days.
        auth.accounts.signOut(account.sub).catch(() => {})
      }
    }
  }, [accounts, me])
}
